import React, { useRef, useState } from 'react';
import { Button, Col, Container, Modal, Row, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { acceptInvitationApi } from '../../api/invitations';
import '../css/login.css';

export const INVITATION_STATUS = {
  PENDING: 'pending',
  ACCEPTING: 'accepting',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
};

const DEFAULT_ERROR =
  'This invitation link is no longer valid. Ask your administrator to send a new one.';
const DEFAULT_SUCCESS = 'Your invitation has been accepted. Sign in to start using Bento.';

// Landing page for the invitation link mailed to a user
// (/invitations/accept/:token). Accepting consumes the token, so it only
// happens on an explicit click: mail scanners and link previews that open the
// URL must not use up the invitation. Either way the only way on is the login
// screen.
const InvitationAccept = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(
    token ? INVITATION_STATUS.PENDING : INVITATION_STATUS.REJECTED
  );
  const [errorMessage, setErrorMessage] = useState(DEFAULT_ERROR);
  const [successMessage, setSuccessMessage] = useState(DEFAULT_SUCCESS);
  // State updates are async, so a fast double click would see PENDING twice;
  // the ref makes sure the token is only ever sent once.
  const submittedRef = useRef(false);

  const acceptInvitation = async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setStatus(INVITATION_STATUS.ACCEPTING);
    try {
      const data = await acceptInvitationApi(token);
      if (typeof data?.message === 'string' && data.message) setSuccessMessage(data.message);
      setStatus(INVITATION_STATUS.ACCEPTED);
    } catch (e) {
      const data = e?.response?.data;
      const message = typeof data === 'string' ? data : data?.message || data?.error;
      setErrorMessage(message || DEFAULT_ERROR);
      setStatus(INVITATION_STATUS.REJECTED);
    }
  };

  const goToLogin = () => navigate('/login');

  const accepted = status === INVITATION_STATUS.ACCEPTED;
  const finished = accepted || status === INVITATION_STATUS.REJECTED;
  const accepting = status === INVITATION_STATUS.ACCEPTING;

  return (
    <div className="Login">
      <Container>
        <Row>
          <Col>
            <Container>
              <Row className="flex-column mt-2">
                <Col className="Login-info-logo">
                  <img src="/bento-logo.svg" alt="Bento Logo" />
                  <span>Bento</span>
                </Col>
                <Col className="Login-info-welcome">Welcome to</Col>
                <h2 className="Login-info-title">Bento</h2>
                <h3 className="Login-info-subtitle">The Benchmarking Tool</h3>
              </Row>
              {!finished && (
                <Row className="mt-4">
                  <Col className="d-flex flex-column align-items-center">
                    <p>You have been invited to join an organization on Bento.</p>
                    <Button
                      variant="primary"
                      className="btn btn-custom dark"
                      onClick={acceptInvitation}
                      disabled={accepting}
                    >
                      {accepting ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" />{' '}
                          Accepting…
                        </>
                      ) : (
                        'Accept Invitation'
                      )}
                    </Button>
                  </Col>
                </Row>
              )}
            </Container>
          </Col>
        </Row>
      </Container>

      <Modal show={finished} backdrop="static" centered>
        <Modal.Dialog>
          <Modal.Header>
            <Modal.Title>{accepted ? 'Invitation Accepted' : 'Invitation Not Valid'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{accepted ? successMessage : errorMessage}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" className="btn btn-custom dark" onClick={goToLogin}>
              Go to Login
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal>
    </div>
  );
};

export default InvitationAccept;
